/* eslint-disable no-unused-expressions */
import { Typography } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, { memo, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useContextReports } from '../../../../../../context/reports';
import { EnumReportTypes, ILoanValue, IReportRow } from '../../../../../../types/reports';
import DraggableListItem from '../../../../../Common/DraggableListItem';
import styles from '../index.module.less';
import { ISettingItem } from '../../../../../../hooks/users';
import { formatAmount } from '../../../../../../utils/formatAmount';
import { prepareLoanDataFromForm } from '../../../../../../utils/prepareData';
import { useReportLoanUpdate } from '../../../../../../hooks/reports';
import { calculateAPR } from '../../../../../../utils/calculateAPR';
import { calculateLoanPoints } from '../../../../../../utils/calculateLoanPoints';
import { calculatePrincipalInterest } from '../../../../../../utils/calculatePrincipalInterest';
import { calculateLoanAmountWithUip } from '../../../../../../utils/calculateLoanAmountWithUip';
import { calculateDownPaymentPrice } from '../../../../../../utils/calculateDownPaymentPrice';
import { calculateLoanClosingCostAmount } from '../../../../../../utils/calculateLoanClosingCostAmount';
import { calculateLoanToValue } from '../../../../../../utils/calculateLoanToValue';
import { calculateDownPayment } from '../../../../../../utils/calculateDownPayment';
import { calculateLoanAmount } from '../../../../../../utils/calculateLoanAmount';
import { calculateHomeInsurance } from '../../../../../../utils/calculateHomeInsurance';
import { calculatePropertyTaxes } from '../../../../../../utils/calculatePropertyTaxes';
import { calculateDepositMoneyPrice } from '../../../../../../utils/calculateDepositMoneyPrice';
import { calculateUip } from '../../../../../../utils/loan';
import { calculateVaFundingFee } from '../../../../../../utils/calculateVaFundingFee';
import { calculateClosingCosts } from '../../../../../../utils/calculateClosingCosts';
import { EnumClosingCostType } from '../../../../../../types/settings';
import { calculatePmi } from '../../../../../../utils/calculatePmi';
import { calculatePmiPercentage } from '../../../../../../utils/calculatePmiPercentage';

const Customize = () => {
  const form = useFormInstance();
  const tableView: IReportRow = useWatch('tableView', form);
  const {
    handleReportSettingsUpdate,
    reportId,
    report,
    reportClosingCosts,
    reportLoanClosingCosts
  } = useContextReports();

  const [itemList, setItemList] = useState<ISettingItem[]>([]);
  const reportLoanUpdate = useReportLoanUpdate();

  useEffect(() => {
    tableView && setItemList([...Object.values(tableView)].sort((a, b) => a.order - b.order));
  }, [tableView]);

  useEffect(() => {
    if (!itemList) return;

    const data: { [key: string]: boolean } = {};

    itemList.forEach((item) => {
      data[item.id] = item.active;
    });

    form.setFieldsValue(data);
  }, [itemList]);

  const handleDragEnd = (droppedItem: DropResult) => {
    if (!droppedItem.destination) return;
    const updatedList = [...itemList];

    const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);

    updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
    const reorderedList = updatedList.map((item, index) => ({ ...item, order: index + 1 }));

    reorderedList.forEach((row, index) => {
      form.setFieldValue(['tableView', row.name], { ...row, order: index + 1 });
    });

    handleReportSettingsUpdate?.('tableView');
    setItemList(reorderedList);
  };

  const handleSettingsUpdate = async (isChecked: boolean, name: string) => {
    if (name === 'uip' ||
      name === 'loanPoints' ||
      name === 'vaFundingFee' ||
      name === 'closingCosts' ||
      name === 'cashOut'
    ) {
      const loans = form.getFieldValue(['loans']) ?? []
      const isRefinance = report?.reportType === EnumReportTypes.refinance;
      const uipActive = form.getFieldValue(['tableView', 'uip', 'active']);
      const vaFundingFeeActive = form.getFieldValue(['tableView', 'vaFundingFee', 'active']);
      const closingCostsActive = form.getFieldValue(['tableView', 'closingCosts', 'active']);
      const cashOutActive = form.getFieldValue(['tableView', 'cashOut', 'active']);
      const loanPointsActive = form.getFieldValue(['tableView', 'loanPoints', 'active']);

      loans.forEach((loan: any, index: number) => {
        // TODO: There need to logic to recalculate Property Value or Purchase Price
        const uip = parseFloat(loan?.uip?.value ?? '0');
        const addUip = loan?.addUip;
        const addVaFundingFee = loan?.externalLoan?.addVaFundingFee;
        const addClosingCosts = loan?.externalLoan?.addClosingCosts;
        const addCashOut = loan?.externalLoan?.addCashOut;
        const vaFundingFee = loan?.externalLoan?.vaFundingFee || 0;
        const loanPoints = parseFloat(loan?.loanPoints?.value ?? '0');
        const closingCosts = parseFloat(loan?.closingCosts?.value ?? '0');
        const cashOut = loan?.externalLoan?.cashOut || 0;
        const closingCostsClearedAt = loan?.externalLoan?.closingCostsClearedAt;

        const uipActiveValue = name === 'uip' ? isChecked : uipActive;
        const vaFundingFeeActiveValue = name === 'vaFundingFee' ? isChecked : vaFundingFeeActive;
        const closingCostsActiveValue = name === 'closingCosts' ? isChecked : closingCostsActive;
        const cashOutActiveValue = name === 'cashOut' ? isChecked : cashOutActive;
        const loanPointsActiveValue = name === 'loanPoints' ? isChecked : loanPointsActive;

        /* eslint-disable no-nested-ternary */
        const value = name === 'uip' ? uip :
          name === 'vaFundingFee' ? vaFundingFee : name === 'closingCosts' ?
            closingCosts : name === 'cashOut' ? cashOut : loanPoints;

        const loanAmount = parseFloat(loan?.loanAmount?.value ?? '0');
        const interestRate = parseFloat(loan?.interestRate?.value ?? '0');
        const loanPeriodInYears = parseFloat(loan?.loanPeriodInYears?.value ?? '0');
        const loanPointsPercentage = parseFloat(loan?.loanPointsPercentage?.value ?? '0');
        let loanAmountWithUip = loanAmount;
        const isFirstItemInRefinance = isRefinance && index === 0;

        const loanClosingCost = reportLoanClosingCosts?.filter(cost => cost.loanId === loan?.id);

        const totalAprRelatedClosingCost = loanClosingCost?.reduce((sum, closingCost) => {
          const reportClosingCost = reportClosingCosts?.find(c => c.name === closingCost.name);

          if (closingCost.name === 'uip' && uipActiveValue) {
            return sum + closingCost.amount
          }

          if (closingCost.name === 'vaFundingFee' && vaFundingFeeActiveValue) {
            return sum + closingCost.amount
          }

          if (closingCost.name === 'loan_points' && loanPointsActiveValue) {
            return sum + closingCost.amount
          }

          if (
            reportClosingCost?.aprIncluded &&
            reportClosingCost?.active &&
            closingCostsActiveValue &&
            !closingCostsClearedAt
          ) {
            const amount = calculateLoanClosingCostAmount(
              loan,
              closingCost.type,
              reportClosingCost?.amount ?? 0,
              closingCost.propertyTaxesMonthsCollected,
              closingCost.daysCollected
            )

            return sum + amount;
          }

          return sum
        }, 0) ?? 0

        const normalizedAprClosingCost = parseFloat(formatAmount(Math.max(0, totalAprRelatedClosingCost)));

        const apr = calculateAPR(loanAmount, interestRate, loanPeriodInYears, normalizedAprClosingCost);

        form.setFieldValue(['loans', index, 'apr', 'value'], apr);
        form.setFieldValue(['loans', index, 'aprRelatedClosingCost', 'value'], normalizedAprClosingCost);

        let totalClosingCosts = closingCosts;

        const shouldSubtract =
          (name === 'uip' && !addUip) ||
          (name === 'vaFundingFee' && !addVaFundingFee) ||
          (name === 'loanPoints');


        if (isChecked) {
          if (shouldSubtract) {
            totalClosingCosts += value;
          }
        } else {
          /* eslint-disable no-lonely-if */
          if (shouldSubtract) {
            totalClosingCosts -= value;
          }
        }

        const formatedClosingCosts = parseFloat(formatAmount(totalClosingCosts))

        if (!isFirstItemInRefinance) {
          form.setFieldValue(['loans', index, 'closingCosts', 'value'], formatedClosingCosts);
        }

        if (name !== 'loanPoints') {
          loanAmountWithUip = calculateLoanAmountWithUip(
            form,
            index,
            {
              closingCosts: formatedClosingCosts,
              uipActive: uipActiveValue,
              vaFundingFeeActive: vaFundingFeeActiveValue,
              closingCostsActive: closingCostsActiveValue,
              cashOutActive: cashOutActiveValue
            }
          )

          form.setFieldValue(['loans', index, 'loanAmountWithUip', 'value'], loanAmountWithUip);

          const loanToValue = calculateLoanToValue(
            form,
            index,
            {
              uipActive: uipActiveValue,
              vaFundingFeeActive: vaFundingFeeActiveValue,
              closingCostsActive: closingCostsActiveValue,
              cashOutActive: cashOutActiveValue
            }
          )

          form.setFieldValue(['loans', index, 'externalLoan', 'loanToValue'], loanToValue);
        }

        const downPaymentPrice = calculateDownPaymentPrice(form, index)

        form.setFieldValue(['loans', index, 'down_payment_price'], downPaymentPrice)

        if (loanPointsPercentage && (addUip && uipActiveValue) ||
          (addVaFundingFee && vaFundingFeeActiveValue) ||
          (addClosingCosts && closingCostsActiveValue && isRefinance) ||
          (addCashOut && cashOutActiveValue)
        ) {
          const calculatedLoanPoints = calculateLoanPoints(
            loanPointsPercentage,
            isChecked ? loanAmountWithUip : loanAmount
          )

          form.setFieldValue(['loans', index, 'loanPoints', 'value'], calculatedLoanPoints);
        }

        const updatedLoan = form.getFieldValue(['loans', index]);
        const principalInterest = calculatePrincipalInterest(updatedLoan)

        form.setFieldValue(['loans', index, 'principalInterest', 'value'], principalInterest)
      })

      const updatedLoans: ILoanValue[] = form?.getFieldValue('loans')

      await reportLoanUpdate.fetch({ loans: prepareLoanDataFromForm(updatedLoans) }, reportId)
    }

    if (name === 'purchasePrice' || name === 'propertyValue') {
      const loans = form.getFieldValue(['loans']) ?? []

      loans.forEach((loan: any, index: number) => {
        let uipValue = 0
        const uipActive = form.getFieldValue(['tableView', 'uip', 'active']);
        const vaFundingFeeActive = form.getFieldValue(['tableView', 'vaFundingFee', 'active']);
        const loanPointsActive = form.getFieldValue(['tableView', 'loanPoints', 'active']);
        const homeInsurancePercentage = form.getFieldValue(['loans', index, 'homeInsurancePercentage']);
        const interestRate = parseFloat(form.getFieldValue(['loans', index, 'interestRate', 'value'])) || 0;
        const purchasePrice = parseFloat(form.getFieldValue(['loans', index, 'purchasePrice', 'value'])) || 0;
        const propertyValue = parseFloat(form.getFieldValue(['loans', index, 'externalLoan', 'propertyValue'])) || 0;
        const loanPeriodInYears = parseFloat(form.getFieldValue(['loans', index, 'loanPeriodInYears', 'value'])) || 0;
        const removeDownPayment = form.getFieldValue(['loans', index, 'externalLoan', 'removeDownPayment']);
        const closingCostsClearedAt = form.getFieldValue(['loans', index, 'externalLoan', 'closingCostsClearedAt']);

        const purchasePriceActive = name === 'purchasePrice' ?
          isChecked : form.getFieldValue(['tableView', 'purchasePrice', 'active']);

        const downPayment = calculateDownPayment(form, index, { purchasePriceActive });
        const loanAmount = calculateLoanAmount(form, index, { downPayment, purchasePriceActive });

        if (homeInsurancePercentage) {
          const homeInsurance = calculateHomeInsurance(form, index, { purchasePriceActive });

          form.setFieldValue(['loans', index, 'homeInsurance', 'value'], homeInsurance);
        }

        const percentage = form.getFieldValue(['loans', index, 'propertyTaxesPercentage', 'value']);

        if (percentage) {
          const propertyTaxes = calculatePropertyTaxes(form, index, { purchasePriceActive });

          form.setFieldValue(['loans', index, 'propertyTaxes', 'value'], propertyTaxes);
        }

        if (!removeDownPayment) {
          form.setFieldValue(['loans', index, 'downPayment', 'value'], downPayment);
        }

        form.setFieldValue(['loans', index, 'deposit_money_price'],
          calculateDepositMoneyPrice(form, index, { purchasePriceActive }));

        form.setFieldValue(['loans', index, 'currentPropertyValue'],
          purchasePriceActive ? purchasePrice : propertyValue);

        const loanClosingCost = reportLoanClosingCosts?.filter(cost => cost.loanId === loan?.id);

        form.setFieldValue(['loans', index, 'loanAmount', 'value'], loanAmount);

        const pmi = calculatePmi(form, index);

        form.setFieldValue(['loans', index, 'pmi', 'value'], pmi);

        const pmiPercentage = calculatePmiPercentage(form, index);

        form.setFieldValue(['loans', index, 'externalLoan', 'pmiPercentage'], pmiPercentage);

        const loanToValue = calculateLoanToValue(form, index, { purchasePriceActive })

        form.setFieldValue(['loans', index, 'externalLoan', 'loanToValue'], loanToValue)

        uipValue = calculateUip(loanAmount);

        form.setFieldValue(['loans', index, 'uip', 'value'], uipValue);

        const vaFundingFeePercentage =
          form.getFieldValue(['loans', index, 'externalLoan', 'vaFundingFeePercentage']) || 0;

        const vaFundingFee = calculateVaFundingFee(loanAmount, vaFundingFeePercentage);

        form.setFieldValue(['loans', index, 'externalLoan', 'vaFundingFee'], vaFundingFee);

        const totalClosingCosts =
          calculateClosingCosts(form, index,reportLoanClosingCosts, reportClosingCosts, { uip: uipValue, vaFundingFee })

        const loanAmountWithUip =
          calculateLoanAmountWithUip(form, index, { uip: uipValue, vaFundingFee, closingCosts: totalClosingCosts  })

        form.setFieldValue(['loans', index, 'loanAmountWithUip', 'value'], loanAmountWithUip);

        form.setFieldValue(['loans', index, 'closingCosts', 'value'], totalClosingCosts)

        const downPaymentPrice = calculateDownPaymentPrice(form, index);

        form.setFieldValue(['loans', index, 'down_payment_price'], downPaymentPrice)

        const updatedLoan = form.getFieldValue(['loans', index]);

        const totalAprRelatedClosingCost = loanClosingCost?.reduce((total, closingCost) => {
          const reportClosingCost = reportClosingCosts?.find(c => c.name === closingCost.name);

          if (closingCost.name === 'uip' && uipActive) {
            return total + uipValue
          }

          if (closingCost.name === 'vaFundingFee' && vaFundingFeeActive) {
            return total + vaFundingFee
          }

          if (closingCost.name === 'loan_points' && loanPointsActive) {
            return total + closingCost.amount
          }

          if (reportClosingCost?.aprIncluded && reportClosingCost?.active && !closingCostsClearedAt) {
            const closingCostAmount = closingCost.type === EnumClosingCostType.flatFee ?
              closingCost.amount : reportClosingCost?.amount ?? 0;

            const amount = calculateLoanClosingCostAmount(
              updatedLoan,
              closingCost.type,
              closingCostAmount,
              closingCost.propertyTaxesMonthsCollected,
              closingCost.daysCollected
            )

            return total + amount;
          }

          return total
        }, 0) ?? 0

        const normalizedAprClosingCost = parseFloat(formatAmount(Math.max(0, totalAprRelatedClosingCost)));

        const apr = calculateAPR(loanAmount, interestRate, loanPeriodInYears, normalizedAprClosingCost);

        form.setFieldValue(['loans', index, 'apr', 'value'], apr);
        form.setFieldValue(['loans', index, 'aprRelatedClosingCost', 'value'], normalizedAprClosingCost);

        const principalInterest = calculatePrincipalInterest(updatedLoan);

        form.setFieldValue(['loans', index, 'principalInterest', 'value'], principalInterest);
      })
    }

    handleReportSettingsUpdate?.('tableView');
  }

  return (
    <div className={styles.popperContent} style={{ borderRight: '1px solid #8d94a2cc', paddingRight: '20px' }}>
      <Typography.Title level={3} style={{ fontSize: 22, fontWeight: '400'}}>
        Loan Comparison List
      </Typography.Title>
      {/* @ts-ignore this works correctly with these props */}
      <DragDropContext onDragEnd={handleDragEnd}>
        {/* @ts-ignore this works correctly with these props */}
        <Droppable droppableId="rows">
          {(provided) => (
            // @ts-ignore this works correctly with these props
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {itemList &&
                itemList.map((row, index) => (
                  // @ts-ignore this works correctly with these props
                  <Draggable key={row.name} draggableId={row.name} index={index}>
                    {(draggableProvided) => (
                      <DraggableListItem
                        provided={draggableProvided}
                        name={['tableView', row.name, 'active']}
                        text={row.text}
                        border={0}
                        padding={4}
                        draggable
                        onChange={handleSettingsUpdate}
                        item={row}
                      />
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default memo(Customize, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
